import { cva, type VariantProps } from 'class-variance-authority';
import { Avatar as AvatarPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@savgroup-front-common/primitive/lib/utils';

export const avatarVariants = cva(
  'group/avatar relative flex shrink-0 select-none after:absolute after:inset-0 after:border after:border-border-base-default after:mix-blend-darken dark:after:mix-blend-lighten',
  {
    variants: {
      shape: {
        circle: 'rounded-full after:rounded-full',
        square: 'rounded-lg after:rounded-lg',
      },
      size: {
        default: 'size-8',
        sm: 'size-6',
        lg: 'size-10',
      },
    },
    defaultVariants: {
      shape: 'circle',
      size: 'default',
    },
  },
);

export function Avatar({
  className,
  shape = 'circle',
  size = 'default',
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-shape={shape}
      data-size={size}
      className={cn(
        'revers-avatar',
        avatarVariants({ shape, size, className }),
      )}
      {...props}
    />
  );
}

export function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn(
        'revers-avatar-image',
        'aspect-square size-full rounded-full object-cover group-data-[shape=square]/avatar:rounded-lg',
        className,
      )}
      {...props}
    />
  );
}

export const avatarFallbackVariants = cva(
  'flex size-full items-center justify-center rounded-full text-sm group-data-[size=sm]/avatar:text-xs group-data-[shape=square]/avatar:rounded-lg',
  {
    variants: {
      variant: {
        'accent-01': 'bg-surface-accent-01-default stext-accent-01-default',
        'accent-02': 'bg-surface-accent-02-default stext-accent-02-default',
        'accent-03': 'bg-surface-accent-03-default stext-accent-03-default',
        'accent-04': 'bg-surface-accent-04-default stext-accent-04-default',
        'accent-05': 'bg-surface-accent-05-default stext-accent-05-default',
        'accent-06': 'bg-surface-accent-06-default stext-accent-06-default',
        'accent-07': 'bg-surface-accent-07-default stext-accent-07-default',
        'accent-08': 'bg-surface-accent-08-default stext-accent-08-default',
        'accent-09': 'bg-surface-accent-09-default stext-accent-09-default',
        'accent-10': 'bg-surface-accent-10-default stext-accent-10-default',
        'accent-11': 'bg-surface-accent-11-default stext-accent-11-default',
        'accent-12': 'bg-surface-accent-12-default stext-accent-12-default',
        'accent-13': 'bg-surface-accent-13-default stext-accent-13-default',
        'accent-14': 'bg-surface-accent-14-default stext-accent-14-default',
        'accent-15': 'bg-surface-accent-15-default stext-accent-15-default',
        'accent-16': 'bg-surface-accent-16-default stext-accent-16-default',
        'accent-17': 'bg-surface-accent-17-default stext-accent-17-default',
        primary: 'bg-surface-primary-subtle-default stext-primary-default hover:bg-surface-primary-subtle-hover',
      },
    },
    defaultVariants: {
      variant: 'accent-01',
    },
  },
);

export function AvatarFallback({
  className,
  variant = 'accent-01',
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback> &
  VariantProps<typeof avatarFallbackVariants>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      data-variant={variant}
      className={cn(
        'revers-avatar-fallback',
        avatarFallbackVariants({ variant, className }),
      )}
      {...props}
    />
  );
}

export function AvatarBadge({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        'revers-avatar-badge',
        'absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-surface-primary-default stext-contrast-default bg-blend-color ring-2 ring-surface-base-default select-none',
        'group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden',
        'group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2',
        'group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2',
        className,
      )}
      {...props}
    />
  );
}

export function AvatarGroup({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        'revers-avatar-group',
        'group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-surface-base-default',
        className,
      )}
      {...props}
    />
  );
}

export function AvatarGroupCount({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        'revers-avatar-group-count',
        'relative flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-muted-default text-sm stext-muted-default ring-2 ring-surface-base-default group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 [&>svg]:size-5 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3',
        className,
      )}
      {...props}
    />
  );
}
